var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
import { React, jsx, DataActionManager, getAppStore, i18n, ReactRedux, css, DataLevel, DataSourceTypes, classNames, MutableStoreManager, hooks } from 'jimu-core';
import { withTheme } from 'jimu-theme';
import { Icon } from './icon';
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem } from './dropdown';
import { Tooltip } from './tooltip';
import { ActionLayersOutlined } from 'jimu-icons/outlined/application/action-layers';
import { ActionOutlined } from 'jimu-icons/outlined/application/action';
import { Loading } from 'jimu-ui';
export var DataActionListStyle;
(function (DataActionListStyle) {
    DataActionListStyle["IconList"] = "ICON_LIST";
    DataActionListStyle["List"] = "LIST";
    DataActionListStyle["Dropdown"] = "DROPDOWN";
})(DataActionListStyle || (DataActionListStyle = {}));
export var IconListDirection;
(function (IconListDirection) {
    IconListDirection["Horizontal"] = "HORIZONTAL";
    IconListDirection["Vertical"] = "VERTICAL";
})(IconListDirection || (IconListDirection = {}));
const getDropdownStyle = (theme) => {
    var _a, _b;
    const primaryColor = (_a = theme === null || theme === void 0 ? void 0 : theme.sys.color) === null || _a === void 0 ? void 0 : _a.primary.main;
    return css `
    .data-action-dropdown {
      .jimu-dropdown-button:hover, [aria-expanded="true"] {
        color: ${(_b = theme === null || theme === void 0 ? void 0 : theme.ref.palette) === null || _b === void 0 ? void 0 : _b.white};
        border-color: ${primaryColor};
        background-color: ${primaryColor};
      }
    }
  `;
};
const getDataActionListStyle = (iconListDirection) => {
    return css `
    .jimu-data-action-icon-list {
      display: flex;
      flex-direction: ${iconListDirection === IconListDirection.Horizontal ? 'row' : 'column'};
      .dropdown-item {
        padding: 0;
        width: 26px;
        height: 26px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .jimu-dropdown-submenu-item {
        padding: 0;
        width: 40px;
        height: 26px;
        display: flex;
        justify-content: center;
        align-items: center;
        .dropdown-item {
          width: 40px;
          .dropdown-button-content {
            flex: inherit;
          }
          .caret-icon {
            margin-left: 4px;
          }
        }
      }
    }

  `;
};
const getLoadingStyle = () => {
    return css `
    @keyframes loading {
      0% {transform: rotate(0deg); };
      100% {transform: rotate(360deg)};
    }
    position: absolute;
    width: 60%;
    height: 60%;
    top: 20%;
    left: 20%;
    border: 2px solid var(--sys-color-secondary-light);
    border-radius: 50%;
    border-top: 2px solid var(--sys-color-primary-main);
    box-sizing: border-box;
    animation:loading 2s infinite linear;
  `;
};
const headerStyle = css `
  height: 2rem;
  background-color: var(--ref-palette-neutral-200);
  color: var(--ref-palette-neutral-1100) !important;
  font-size: 14px;
  line-height: 2rem;
  display: flex !important;
  align-items: center;
`;
const getRecordsCount = (dataSets) => {
    let count = 0;
    for (const dataSet of dataSets) {
        count += dataSet.records.length;
    }
    return count;
};
const getAvailableActions = (dataSets, widgetId, extraActions) => __awaiter(void 0, void 0, void 0, function* () {
    // If no records, return empty record action list
    let recordActions = {};
    const recordsCount = getRecordsCount(dataSets);
    if (recordsCount !== 0) {
        recordActions = yield DataActionManager.getInstance().getSupportedActions(widgetId, dataSets, DataLevel.Records);
    }
    let dsActions = {};
    if (dataSets.length > 0) {
        dsActions = yield DataActionManager.getInstance().getSupportedActions(widgetId, dataSets, DataLevel.DataSource);
    }
    // Support extra actions that are not registered in the DataActionManager
    if (extraActions && extraActions.length > 0) {
        for (const action of extraActions) {
            if (yield action.isSupported(dataSets, DataLevel.Records, widgetId)) {
                recordActions[action.id] = [action];
            }
            if (yield action.isSupported(dataSets, DataLevel.DataSource, widgetId)) {
                dsActions[action.id] = [action];
            }
        }
    }
    return [recordActions, dsActions];
});
const getRecordsAttributes = (dataSets) => {
    let count = getRecordsCount(dataSets);
    let recordLabel = i18n.getIntl().formatMessage({ id: 'data' });
    if (dataSets.length === 0) {
        return {
            recordLabel,
            count
        };
    }
    switch (dataSets[0].type) {
        case 'loaded': {
            recordLabel = i18n.getIntl().formatMessage({ id: 'loadedData' }, { count });
            break;
        }
        case 'selected': {
            recordLabel = i18n.getIntl().formatMessage({ id: 'selectedData' }, { count });
            break;
        }
        case 'current': {
            recordLabel = i18n.getIntl().formatMessage({ id: 'currentData' });
            count = 1;
            break;
        }
    }
    return {
        recordLabel,
        count
    };
};
const DataActionListComponent = (props) => {
    const { widgetId, dataSets, listStyle = DataActionListStyle.List, buttonType = 'default', buttonSize = 'sm', theme, iconListDirection = IconListDirection.Horizontal, disableDataSourceLevelActions = false, hideGroupTitle = false, shouldHideEmptyList = false, onActionListItemClick, alwaysShowBatchIcon = false, actionPanelRefDOM, buttonClassName = '', extraActions } = props;
    const [recordActionGroups, setRecordActionGroups] = React.useState({});
    const [dsActionGroups, setDsActionGroups] = React.useState({});
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [isDropdownLoading, setIsDropdownLoading] = React.useState(false);
    const [actionElement, setActionElement] = React.useState(null);
    const [dsCount, setDsCount] = React.useState('...');
    const [hasCountableLayerOnly, setHasCountableLayerOnly] = React.useState(true);
    const [isListLoading, setIsListLoading] = React.useState(true);
    const dropdownRef = React.useRef(null);
    const appWidgets = ReactRedux.useSelector((state) => state.appConfig.widgets);
    ReactRedux.useSelector((state) => state.widgetsMutableStateVersion);
    const useBatchIcon = alwaysShowBatchIcon || dataSets.length > 1;
    const refreshDataActionList = hooks.useEventCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!dataSets) {
            return;
        }
        const [newRecordActionGroups, newDsActionGroups] = yield getAvailableActions(dataSets, widgetId, extraActions);
        const prevRecordKeys = Object.keys(recordActionGroups);
        const prevDsKeys = Object.keys(dsActionGroups);
        const currRecordKeys = Object.keys(newRecordActionGroups);
        let currDsActionKeys = Object.keys(newDsActionGroups);
        const recordKeySet = new Set(currRecordKeys);
        const dsKeySet = new Set(currDsActionKeys);
        if (getRecordsAttributes(dataSets).count === dsCount) {
            // Delete actions in ds when count are the same
            for (const action of dsKeySet) {
                if (recordKeySet.has(action)) {
                    delete newDsActionGroups[action];
                }
            }
            currDsActionKeys = Object.keys(newDsActionGroups);
        }
        // Update the state when the length or order changed
        if (prevDsKeys.length !== currDsActionKeys.length || prevRecordKeys.length !== currRecordKeys.length) {
            if (prevDsKeys.length !== currDsActionKeys.length) {
                setDsActionGroups(newDsActionGroups);
            }
            if (prevRecordKeys.length !== currRecordKeys.length) {
                setRecordActionGroups(newRecordActionGroups);
            }
        }
        else {
            for (let i = 0; i < prevRecordKeys.length; i++) {
                const prevKey = prevRecordKeys[i];
                const currKey = currRecordKeys[i];
                // Only when the action's name and length are the same
                if (prevKey === currKey && ((_a = prevRecordKeys[prevKey]) === null || _a === void 0 ? void 0 : _a.length) === ((_b = currRecordKeys[currKey]) === null || _b === void 0 ? void 0 : _b.length)) {
                    continue;
                }
                else {
                    setRecordActionGroups(newRecordActionGroups);
                }
            }
            for (let i = 0; i < prevDsKeys.length; i++) {
                const prevKey = prevDsKeys[i];
                const currKey = currDsActionKeys[i];
                // Only when the action's name and length are the same
                if (prevKey === currKey && ((_c = prevDsKeys[prevKey]) === null || _c === void 0 ? void 0 : _c.length) === ((_d = currDsActionKeys[currKey]) === null || _d === void 0 ? void 0 : _d.length)) {
                    continue;
                }
                else {
                    setDsActionGroups(newDsActionGroups);
                }
            }
        }
    }));
    React.useEffect(() => {
        function queryCount() {
            return __awaiter(this, void 0, void 0, function* () {
                // Do not query count if it's a closed dropdown style OR the title is hidden
                if ((listStyle === DataActionListStyle.Dropdown && !isDropdownOpen) || hideGroupTitle) {
                    return;
                }
                const COUNTABLE_LAYER_TYPES = [DataSourceTypes.FeatureLayer, DataSourceTypes.ImageryLayer, DataSourceTypes.OrientedImageryLayer, DataSourceTypes.SubtypeGroupLayer, DataSourceTypes.SubtypeSublayer];
                if (dataSets.some(dataSet => {
                    return !COUNTABLE_LAYER_TYPES.includes(dataSet.dataSource.type);
                })) {
                    setHasCountableLayerOnly(false);
                    return;
                }
                const canUseDss = dataSets.map(dataSet => dataSet.dataSource).filter(ds => ds.loadCount);
                const counts = yield (Promise.all(canUseDss.map(ds => ds.loadCount({}, { widgetId: 'data-action-list' }))));
                let sum = 0;
                for (const count of counts) {
                    sum += count;
                }
                setDsCount(sum);
            });
        }
        queryCount();
    }, [dataSets, hideGroupTitle, isDropdownOpen, listStyle]);
    React.useEffect(() => {
        function appendObjectIdField() {
            for (const dataSet of dataSets) {
                if (dataSet.fields) {
                    const ds = dataSet.dataSource;
                    const dsIdField = ds.getIdField();
                    if (!dataSet.fields.includes(dsIdField)) {
                        dataSet.fields.push(dsIdField);
                    }
                }
            }
        }
        appendObjectIdField();
    }, [dataSets]);
    React.useEffect(() => {
        if (props.listStyle === DataActionListStyle.Dropdown) {
            return;
        }
        const asyncRefreshHelper = () => __awaiter(void 0, void 0, void 0, function* () {
            props.listStyle !== DataActionListStyle.Dropdown && setIsListLoading(true);
            yield refreshDataActionList();
            props.listStyle !== DataActionListStyle.Dropdown && setIsListLoading(false);
        });
        // Listen to the widgets change so it could get latest data-action list
        asyncRefreshHelper();
    }, [appWidgets, props.listStyle, refreshDataActionList]);
    React.useEffect(() => {
        const watchingActions = DataActionManager.getInstance().getActions().filter(action => action.isWatchingDataSetsChange);
        watchingActions.forEach(action => {
            if (action.onDataSetsChange) {
                action.onDataSetsChange(dataSets, widgetId);
            }
        });
    }, [dataSets, widgetId]);
    const getWidget = (widgetId) => {
        return getAppStore().getState().appConfig.widgets[widgetId];
    };
    const getActionLabel = (actionName, firstAction) => {
        if (firstAction.widgetId) {
            return firstAction.label;
        }
        return i18n.getIntl().formatMessage({ id: actionName });
    };
    const onActionItemClick = (evt, action, dataLevel) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const ACTIVE_CLASSNAME = 'active-data-action-item';
        const prevActive = document.querySelector(`.${ACTIVE_CLASSNAME}`);
        if (prevActive) {
            // Clean up the active className first
            prevActive.classList.remove(ACTIVE_CLASSNAME);
        }
        if (actionPanelRefDOM) {
            // If there's a valid action panel reference, add the class name to the node directly
            actionPanelRefDOM.classList.add(ACTIVE_CLASSNAME);
        }
        else if (isDropdownStyle) {
            // When it's a dropdown style, append the class to the dropdown button, otherwise append it to the actual item
            dropdownRef.current.className = classNames(dropdownRef.current.className, ACTIVE_CLASSNAME);
        }
        else {
            // Do not use ClassNames function, it can not handle svg element
            evt.target.classList.add(ACTIVE_CLASSNAME);
        }
        const widget = getWidget(widgetId);
        const dataActionJson = (_a = widget.dataActions) === null || _a === void 0 ? void 0 : _a[action.name];
        // Execute the data action
        const actionElement = yield DataActionManager.getInstance().executeDataAction(action, dataSets, dataLevel, widgetId, dataActionJson === null || dataActionJson === void 0 ? void 0 : dataActionJson.config);
        // This is used for close the modal
        if (actionElement !== null && typeof actionElement !== 'boolean') {
            setActionElement(React.cloneElement(actionElement, {
                onClose: () => { setActionElement(null); },
                onConfirm: (...args) => {
                    !actionElement.props.keepOpenAfterConfirm && setActionElement(null);
                    return actionElement.props.onConfirm(...args);
                }
            }));
        }
        // If custom action click handler is passed in, try to invoke it
        onActionListItemClick && onActionListItemClick();
        setIsDropdownOpen(false);
    });
    const onDropDownToggle = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!isDropdownOpen) { // open the dropdown menu
            setIsDropdownLoading(true);
            try {
                const [recordActions, dsActions] = yield getAvailableActions(dataSets, widgetId, extraActions);
                setRecordActionGroups(recordActions);
                setDsActionGroups(dsActions);
            }
            catch (err) {
                console.error(err);
                setRecordActionGroups({});
                setDsActionGroups({});
            }
            setIsDropdownLoading(false);
            setIsDropdownOpen(true);
        }
        else {
            setIsDropdownOpen(false);
        }
    });
    const createActionItem = (actionGroups, actionName, dataLevel, isIconList) => {
        const actions = actionGroups[actionName];
        if ((actions === null || actions === void 0 ? void 0 : actions.length) > 0 && dataSets.length > 0) {
            const firstAction = actions[0];
            const dsId = dataSets[0].dataSource.id;
            const icon = MutableStoreManager.getInstance().getStateValue(['dataActions', firstAction.name, dsId, 'icon']) || firstAction.icon;
            if (actions.length === 1) {
                return (jsx(DropdownItem, { key: firstAction.id, header: false, "aria-label": firstAction.label, onClick: (e) => __awaiter(void 0, void 0, void 0, function* () { yield onActionItemClick(e, firstAction, dataLevel); }) },
                    jsx("div", { className: 'd-flex align-items-center' },
                        icon && jsx(React.Fragment, null, (typeof icon === 'string')
                            ? jsx(Icon, { icon: icon, className: 'jimu-icon-auto-color mr-2' })
                            : icon),
                        !isIconList && jsx("span", null, firstAction.label))));
            }
            else {
                const actionLabel = getActionLabel(actionName, firstAction);
                return (jsx(Dropdown, { key: firstAction.id, direction: isIconList ? 'down' : 'right', isSubMenuItem: true, fluid: true },
                    jsx(DropdownButton, { arrowRight: !isIconList, "aria-label": actionLabel, size: buttonSize, type: buttonType },
                        jsx("div", { className: 'd-flex align-items-center' },
                            icon && jsx(Icon, { icon: icon, className: 'jimu-icon-auto-color' }),
                            !isIconList && jsx("span", null, actionLabel))),
                    jsx(DropdownMenu, null, actions.map((action, index) => {
                        var _a;
                        let label = action.label;
                        if (action.widgetId) {
                            if (action.widgetId === widgetId) {
                                label = i18n.getIntl().formatMessage({ id: 'current' });
                            }
                            else {
                                const widget = getWidget(action.widgetId);
                                label = (_a = widget === null || widget === void 0 ? void 0 : widget.label) !== null && _a !== void 0 ? _a : action.label;
                            }
                        }
                        return (jsx(DropdownItem, { key: index, header: false, onClick: (e) => __awaiter(void 0, void 0, void 0, function* () { yield onActionItemClick(e, action, dataLevel); }) }, label));
                    }))));
            }
        }
        return null;
    };
    const recordActionNames = Object.keys(recordActionGroups);
    const dsActionNames = Object.keys(dsActionGroups);
    const isIconStyle = (listStyle === DataActionListStyle.IconList);
    const isDropdownStyle = (listStyle === DataActionListStyle.Dropdown);
    const recordActionContent = (recordActionNames === null || recordActionNames === void 0 ? void 0 : recordActionNames.length) > 0 ? recordActionNames.map(actionName => createActionItem(recordActionGroups, actionName, DataLevel.Records, isIconStyle)) : [];
    const dsActionContent = (!disableDataSourceLevelActions && (dsActionNames === null || dsActionNames === void 0 ? void 0 : dsActionNames.length) > 0) ? dsActionNames.map(actionName => createActionItem(dsActionGroups, actionName, DataLevel.DataSource, isIconStyle)) : [];
    const isNoActions = (disableDataSourceLevelActions && (recordActionContent === null || recordActionContent === void 0 ? void 0 : recordActionContent.length) === 0) || ((recordActionContent === null || recordActionContent === void 0 ? void 0 : recordActionContent.length) === 0 && (dsActionContent === null || dsActionContent === void 0 ? void 0 : dsActionContent.length) === 0);
    const hideTitle = hideGroupTitle && ((recordActionContent === null || recordActionContent === void 0 ? void 0 : recordActionContent.length) === 0 || (dsActionContent === null || dsActionContent === void 0 ? void 0 : dsActionContent.length) === 0);
    let noActionContent = null;
    if (isNoActions && !shouldHideEmptyList) {
        noActionContent = (jsx(DropdownItem, { header: true },
            jsx("div", { className: 'd-flex align-items-center' },
                jsx("span", null, i18n.getIntl().formatMessage({ id: 'noActions' })))));
    }
    const listContent = jsx(React.Fragment, null,
        noActionContent,
        (recordActionContent === null || recordActionContent === void 0 ? void 0 : recordActionContent.length) > 0 && (jsx(React.Fragment, null,
            !hideTitle && jsx(DropdownItem, { "data-testid": "record-title", header: true, css: headerStyle }, getRecordsAttributes(dataSets).recordLabel),
            recordActionContent)),
        (!disableDataSourceLevelActions && (dsActionContent === null || dsActionContent === void 0 ? void 0 : dsActionContent.length) > 0) && (jsx(React.Fragment, null,
            !hideTitle &&
                jsx(DropdownItem, { "data-testid": "dataSource-title", header: true, css: headerStyle }, hasCountableLayerOnly
                    ? i18n.getIntl().formatMessage({ id: 'allData' }, { count: dsCount })
                    : i18n.getIntl().formatMessage({ id: 'allDataWithoutCount' })),
            dsActionContent)));
    if (isDropdownStyle) {
        const tooltip = i18n.getIntl().formatMessage({ id: 'actions' });
        // Wrap the content with Dropdown component
        return (jsx("div", { css: getDropdownStyle(theme) },
            jsx(Dropdown, { direction: 'down', size: 'sm', toggle: onDropDownToggle, isOpen: isDropdownOpen, className: 'data-action-list-dropdown' },
                jsx(Tooltip, { title: tooltip, placement: 'top' },
                    jsx(DropdownButton, { icon: true, size: buttonSize, type: buttonType, disabled: isDropdownLoading, arrow: false, ref: dropdownRef, className: classNames('data-action-button', buttonClassName) },
                        !isDropdownLoading && (useBatchIcon ? jsx(ActionLayersOutlined, { "aria-hidden": true }) : jsx(ActionOutlined, { "aria-hidden": true })),
                        isDropdownLoading && jsx("div", { css: getLoadingStyle(), "data-testid": 'data-action-loading-btn' }))),
                jsx(DropdownMenu, null, listContent)),
            actionElement));
    }
    else {
        const content = jsx(React.Fragment, null,
            jsx("div", { className: isIconStyle ? 'jimu-data-action-icon-list' : '' }, listContent),
            actionElement);
        return (jsx("div", { css: getDataActionListStyle(iconListDirection) }, isListLoading ? jsx(Loading, { type: 'DOTS_PRIMARY' }) : content));
    }
};
/** This component displays available data action in a list, icon list, or dropdown style.
 * Widgets that need to use DataAction can use this component.
 * To display in a specific style, just pass `listStyle` with ‘List', ‘IconList’, or ‘Dropdown' provided in the `DataActionListStyle`
 *
 * You can use this component by:
 *
 * `import { DataActionList } from 'jimu-ui'`
 */
export const DataActionList = withTheme(DataActionListComponent);
